import { ArrowRight, Calculator, Clock3, Footprints, Leaf, Ruler, ShieldCheck, SunMedium } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "../app/router";
import { PageMeta } from "../components/PageMeta";
import { SectionHeading } from "../components/SectionHeading";
import { styles } from "../data/taxonomy";
import { assetUrl } from "../lib/asset";
import { readSavedDesign } from "../lib/storage";

const signals = [
  { icon: SunMedium, title: "Đúng vi khí hậu", body: "Nắng, hướng và gió quyết định cây nào thực sự ở lại." },
  { icon: Footprints, title: "Đủ chỗ để sống", body: "Giữ dải lưu thông và chỗ thao tác tưới trước khi thêm đồ." },
  { icon: Calculator, title: "Vừa ngân sách", body: "Mỗi thay đổi đi thẳng vào tổng, không có hệ số ẩn." },
  { icon: ShieldCheck, title: "An toàn nếp nhà", body: "Lọc theo trẻ nhỏ, thú cưng, chậu treo và tải trọng cần khảo sát." },
];

export function HomePage() {
  const [hasSaved, setHasSaved] = useState(() => Boolean(readSavedDesign()));
  useEffect(() => {
    const listener = () => setHasSaved(Boolean(readSavedDesign()));
    window.addEventListener("reunang-storage", listener);
    return () => window.removeEventListener("reunang-storage", listener);
  }, []);

  return (
    <>
      <PageMeta
        title="Rêu Nắng — Thiết kế ban công sống khỏe"
        description="Phác phương án cây, chậu và góc ngồi hợp nắng, công chăm và ngân sách của căn hộ."
      />
      {hasSaved && (
        <div className="saved-strip" role="status">
          <div className="container">
            <span>Bạn có một bản phác đã cất trên thiết bị này.</span>
            <Link to="/thiet-ke?restore=1">Mở lại bản phác <ArrowRight size={16} aria-hidden="true" /></Link>
          </div>
        </div>
      )}
      <section className="hero">
        <div className="container hero-grid">
          <div className="hero-copy">
            <span className="eyebrow">Thiết kế ban công xanh có căn cứ</span>
            <h1>
              Một khoảng xanh vừa vặn với <em>nắng</em> — và với nhịp sống của bạn.
            </h1>
            <p className="lead">
              Nhập kích thước, hướng nắng và thời gian bạn thật sự muốn chăm. Rêu Nắng ghép cây, chậu,
              góc ngồi và phụ kiện thành một phương án có giá và lịch chăm rõ ràng.
            </p>
            <div className="hero-actions">
              <Link className="button button-primary button-large" to="/thiet-ke">
                Phác ban công của tôi <ArrowRight aria-hidden="true" />
              </Link>
              <Link className="button button-ghost button-large" to="/cam-nang">
                Xem cách công cụ chọn cây
              </Link>
            </div>
            <ul className="proof-list" aria-label="Thông tin nhanh">
              <li><Leaf aria-hidden="true" /> Dữ liệu cố định</li>
              <li><ShieldCheck aria-hidden="true" /> Không cần đăng nhập</li>
              <li><Clock3 aria-hidden="true" /> 3–5 phút</li>
            </ul>
          </div>
          <figure className="hero-media">
            <img
              src={assetUrl("/media/hero-ban-cong.webp")}
              width="1586"
              height="992"
              alt="Ban công nhỏ có ghế đọc và các tầng cây trong nắng sáng"
            />
            <figcaption>
              <span>Phương án mẫu</span>
              <strong>3,2 × 1,45 m · Đông Nam</strong>
              <small>Ảnh tạo riêng cho thương hiệu hư cấu</small>
            </figcaption>
          </figure>
        </div>
      </section>

      <section className="section section-signals">
        <div className="container">
          <SectionHeading
            eyebrow="Không chỉ hợp ảnh"
            title="Bốn điều một moodboard thường bỏ quên."
            body="Ban công sống khỏe bắt đầu từ nắng, gió, lối đi và nhịp chăm — rồi mới đến câu chuyện đẹp."
          />
          <div className="signal-grid">
            {signals.map(({ icon: Icon, title, body }, index) => (
              <article className="signal-card" key={title}>
                <div className="signal-top">
                  <span>0{index + 1}</span>
                  <Icon aria-hidden="true" />
                </div>
                <h3>{title}</h3>
                <p>{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section style-section">
        <div className="container">
          <SectionHeading
            eyebrow="Ba nếp xanh"
            title="Chọn một cảm giác, không chọn một khuôn mẫu."
            body="Mỗi nếp xanh là điểm xuất phát; công cụ vẫn đổi cây và vật liệu theo điều kiện thật."
            action={<Link className="text-link" to="/phong-cach">So sánh ba nếp <ArrowRight size={18} aria-hidden="true" /></Link>}
          />
          <div className="style-card-grid">
            {styles.map((style, index) => (
              <article className="style-card" key={style.id}>
                <div className="style-media">
                  <img
                    src={style.image}
                    width="1448"
                    height="1086"
                    loading="lazy"
                    alt={`Ban công phong cách ${style.name.toLowerCase()}`}
                  />
                  <span>0{index + 1}</span>
                </div>
                <div className="style-card-body">
                  <div className="palette" aria-label={`Bảng màu ${style.name}`}>
                    {style.palette.map((color) => <i key={color} style={{ background: color }} />)}
                  </div>
                  <h3>{style.name}</h3>
                  <p>{style.description}</p>
                  <Link to={`/thiet-ke?style=${style.id}`}>Phác với nếp này <ArrowRight size={16} aria-hidden="true" /></Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section output-section">
        <div className="container output-grid">
          <div className="output-image-wrap">
            <img
              src={assetUrl("/media/plan-nang-thanh-thoi.webp")}
              width="1586"
              height="992"
              loading="lazy"
              alt="Phương án Nắng Thảnh Thơi với ghế gập và cụm cây dọc lan can"
            />
            <span className="image-note note-one">Lối đi 60+ cm</span>
            <span className="image-note note-two">7 cây · chăm 43 phút</span>
          </div>
          <div className="output-copy">
            <span className="eyebrow">Một con số có thể lần theo</span>
            <h2>Thấy cả vẻ đẹp lẫn phần phải chăm.</h2>
            <p>
              Bản phác đi cùng từng dòng giá, số cây, khoảng ngân sách còn lại và lịch việc theo tuần.
              Bạn có thể bỏ ghế, đổi gói hoặc thêm chậu — tổng cập nhật ngay.
            </p>
            <div className="mini-estimate">
              <div><span>Cây & chậu</span><strong>4.810.000 đ</strong></div>
              <div><span>Nội thất & phụ kiện</span><strong>4.270.000 đ</strong></div>
              <div><span>Gói Vừa Vặn</span><strong>2.400.000 đ</strong></div>
              <div className="mini-total"><span>Dự toán mẫu</span><strong>11.480.000 đ</strong></div>
            </div>
            <Link className="button button-secondary" to="/thiet-ke">
              Mở workspace <ArrowRight aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      <section className="section process-section">
        <div className="container">
          <SectionHeading eyebrow="Từ đo đến một cuộc trò chuyện" title="Bốn nhịp, không có bước mù." align="center" />
          <ol className="process-list">
            {[
              ["Đo & kể", "Kích thước, nắng, gió, ngân sách và nếp sống."],
              ["Nhận phác", "Một bố cục được ghép từ dữ liệu cố định, có lý do."],
              ["Chỉnh cho vừa", "Thêm, bớt, chuyển vùng; giá và lịch chăm đi cùng."],
              ["Gửi tư vấn", "Nhận biên nhận mô phỏng; không dữ liệu nào rời máy."],
            ].map(([title, body], index) => (
              <li key={title}>
                <span>{index + 1}</span>
                <h3>{title}</h3>
                <p>{body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="section safety-section">
        <div className="container safety-card">
          <div>
            <span className="eyebrow">Giới hạn được nói rõ</span>
            <h2>Đẹp chưa đủ. Ban công còn phải chịu được đời sống thật.</h2>
          </div>
          <div className="safety-points">
            <p><Ruler aria-hidden="true" /><span><strong>Tải trọng cần khảo sát.</strong> Demo chỉ nhắc rủi ro, không kết luận kết cấu.</span></p>
            <p><ShieldCheck aria-hidden="true" /><span><strong>Thú cưng được lọc trước.</strong> Cây không an toàn sẽ không vào đề xuất khi bạn bật ràng buộc.</span></p>
            <p><Footprints aria-hidden="true" /><span><strong>Lối đi có ưu tiên.</strong> Công cụ cảnh báo khi mặt sàn quá dày hoặc khoảng thao tác hẹp.</span></p>
          </div>
        </div>
      </section>

      <section className="section final-cta">
        <div className="container">
          <SunMedium aria-hidden="true" />
          <span className="eyebrow">Bắt đầu từ điều đang có</span>
          <h2>Ban công của bạn đang có nắng kiểu nào?</h2>
          <p>Dùng cấu hình mẫu hoặc nhập kích thước thật. Không cần đăng nhập.</p>
          <Link className="button button-sun button-large" to="/thiet-ke">
            Phác trong 3–5 phút <ArrowRight aria-hidden="true" />
          </Link>
        </div>
      </section>
    </>
  );
}
